class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.onMaisonDrawerClick.bind(this));
    this.addEventListener('submit', this.onMaisonDrawerSubmit.bind(this));
    this.querySelector('.drawer__inner')?.setAttribute('aria-hidden', 'true');
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-expanded', 'false');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  onMaisonDrawerClick(event) {
    const toggle = event.target.closest('[data-maison-promo-toggle]');
    if (!toggle || !this.contains(toggle)) return;

    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    this.setMaisonPromoOpen(toggle, !isOpen);
  }

  onMaisonDrawerSubmit(event) {
    const form = event.target.closest('[data-maison-promo-form]');
    if (!form || !this.contains(form)) return;

    event.preventDefault();
    this.applyMaisonPromoCode(form);
  }

  setMaisonPromoOpen(toggle, isOpen) {
    const panelId = toggle.getAttribute('aria-controls');
    const panel = panelId ? this.querySelector(`#${panelId}`) : null;
    toggle.setAttribute('aria-expanded', String(isOpen));
    panel?.classList.toggle('is-open', isOpen);

    if (isOpen) {
      window.setTimeout(() => panel?.querySelector('input')?.focus(), 320);
    }
  }

  setMaisonPromoStatus(form, message) {
    const status = form?.querySelector('[data-maison-promo-status]');
    if (status) status.textContent = message;
  }

  async applyMaisonPromoCode(form) {
    if (this.maisonPromoInFlight) return;

    const input = form.querySelector('input[name="discount"]');
    const submit = form.querySelector('[data-maison-promo-apply]');
    const toggle = form.querySelector('[data-maison-promo-toggle]');
    const code = input?.value.trim() || '';

    if (!code) {
      if (toggle) this.setMaisonPromoOpen(toggle, true);
      this.setMaisonPromoStatus(form, 'Enter a promo code.');
      input?.focus();
      return;
    }

    this.maisonPromoInFlight = true;
    if (submit) {
      submit.disabled = true;
      submit.textContent = 'APPLYING';
    }
    this.setMaisonPromoStatus(form, 'Checking code…');

    try {
      const response = await fetch(routes.cart_update_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          discount: code,
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok) throw new Error(state.description || state.message || 'Unable to apply promo code.');

      const discountCodes = Array.isArray(state.discount_codes) ? state.discount_codes : [];
      const discountCode = discountCodes.find((entry) => String(entry.code || '').toLowerCase() === code.toLowerCase());
      const applications = Array.isArray(state.cart_level_discount_applications)
        ? state.cart_level_discount_applications
        : [];
      const applied = discountCode ? discountCode.applicable !== false : applications.length > 0;
      const message = applied ? 'Promo code applied.' : 'This code is not valid for your cart.';

      if (state.sections?.['cart-drawer'] && state.sections?.['cart-icon-bubble']) {
        this.classList.toggle('is-empty', state.item_count === 0);
        this.renderContents(state);
        window.setTimeout(() => {
          const nextForm = this.querySelector('[data-maison-promo-form]');
          const nextToggle = nextForm?.querySelector('[data-maison-promo-toggle]');
          const nextInput = nextForm?.querySelector('input[name="discount"]');
          if (nextInput) nextInput.value = code;
          if (nextToggle) this.setMaisonPromoOpen(nextToggle, true);
          this.setMaisonPromoStatus(nextForm, message);
        }, 60);
      } else {
        this.setMaisonPromoStatus(form, message);
      }
    } catch (error) {
      this.setMaisonPromoStatus(form, error.message || 'Unable to apply promo code.');
    } finally {
      this.maisonPromoInFlight = false;
      if (submit?.isConnected) {
        submit.disabled = false;
        submit.textContent = 'APPLY';
      }
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    const inner = this.querySelector('.drawer__inner');
    inner?.setAttribute('aria-hidden', 'false');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'true');

    window.setTimeout(() => {
      this.classList.add('animate', 'active');
      const cartDrawerItems = this.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        cartDrawerItems.updateCartUpsellToggleState();
        cartDrawerItems.updateCartUpsellVisibility();
      }
    });

    let focusTrapped = false;
    const trapDrawerFocus = () => {
      if (focusTrapped || !this.classList.contains('active')) return;
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__close') || inner;
      if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      focusTrapped = true;
    };
    const onTransitionEnd = (event) => {
      if (event.target !== inner || event.propertyName !== 'transform') return;
      this.removeEventListener('transitionend', onTransitionEnd);
      trapDrawerFocus();
    };
    this.addEventListener('transitionend', onTransitionEnd);
    window.setTimeout(trapDrawerFocus, 800);

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.querySelector('.drawer__inner')?.setAttribute('aria-hidden', 'true');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const inner = this.querySelector('.drawer__inner');
    inner?.classList.remove('is-empty');
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionElement || !sectionHtml) return;

      const nextHtml = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (nextHtml !== null) sectionElement.innerHTML = nextHtml;
    });

    window.setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
